// REACT
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

// MATERIAL UI
import { Box, Container, Stack } from "@mui/material";

// COMPONENTS
import { AlertCard, AlertStyleType } from "src/components/alerts/AlertCard";
import ContainedButton from "src/components/common/ContainedButton";
import Title from "src/components/common/Title";
import Layout from "src/components/core/Layout";
import TaxDocumentationWidget from "src/components/tax-center/TaxDocumentationWidget";
import TaxDocumentsWidget from "src/components/tax-center/TaxDocumentsWidget";
import TaxSettingsSummaryCard from "src/components/tax-center/TaxSettingsSummaryCard";
import TransactionReviewSummaryCard from "src/components/tax-center/TransactionReviewSummaryCard";

// REDUX
import api from "src/store/features/api";

// STYLES & UTILS
import { buildTaxTransactionsArg } from "src/components/tax-center/utils";

const TaxCenter = () => {
  const history = useHistory();
  const taxYear = new Date().getUTCFullYear();
  const queryArg = useMemo(() => buildTaxTransactionsArg(taxYear), [taxYear]);
  const transactionsQuery = api.useGetTaxTransactionsQuery(queryArg, { refetchOnFocus: false });

  const transactions = transactionsQuery.data?.data ?? [];
  const needsReview = transactions.filter((item) => item.missing_cost_basis).length;
  const declared = transactions.filter((item) => !item.missing_cost_basis).length;

  const goToReview = () => history.push("/tax-center/transactions");
  const goToSettings = () => history.push("/tax-center/settings");

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "flex-end" }}
            justifyContent="space-between"
            gap={3}>
            <Box>
              <Title
                title={{ partA: "Tax", partB: "Center" }}
                subtitle="Provide cost basis, access tax forms and set how your gains are calculated."
              />
            </Box>
            <Stack direction="row" gap={1.25}>
              {needsReview > 0 && (
                <ContainedButton color="salmon" width="190px" height="40px" onClick={goToReview}>
                  {`Review ${needsReview} deposit${needsReview === 1 ? "" : "s"}`}
                </ContainedButton>
              )}
            </Stack>
          </Stack>

          {needsReview > 0 && (
            <Box sx={{ "& > div": { marginBottom: 0 } }}>
              <AlertCard
                styleType={AlertStyleType.WARNING}
                titleText={`${needsReview} deposit${needsReview === 1 ? " is" : "s are"} missing cost basis`}
                subText={`Deposits without cost basis are reported to the IRS with an acquisition cost of $0. The cutoff for the ${taxYear} tax year is 31 Jan ${taxYear + 1}.`}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 2,
            }}>
            <TransactionReviewSummaryCard
              needsReview={needsReview}
              declared={declared}
              taxYear={taxYear}
              loading={transactionsQuery.isLoading}
              error={transactionsQuery.isError}
              onOpen={goToReview}
            />
            <TaxDocumentationWidget />
            <TaxDocumentsWidget />
            <TaxSettingsSummaryCard onOpen={goToSettings} />
          </Box>
        </Stack>
      </Container>
    </Layout>
  );
};

export default TaxCenter;
